import { useState, useCallback } from 'react';
import { parseISO, addDays } from 'date-fns';
import { PlanningMode } from '../types/monEspace.types';
import { getWeekStart } from '../api/monEspace.api';

/**
 * Hook for managing planning mode state (recurrent vs week)
 * and current week selection
 */
export const usePlanningMode = (initialWeek?: string | null) => {
  const [mode, setMode] = useState<PlanningMode>('week');
  const [currentWeekStart, setCurrentWeekStart] = useState<string>(() => {
    // When opened from a "Valider" action we land directly on the target week.
    const base = initialWeek ? parseISO(initialWeek) : new Date();
    return getWeekStart(base);
  });

  /**
   * Switch to a different planning mode
   */
  const switchMode = useCallback((newMode: PlanningMode) => {
    setMode(newMode);
  }, []);

  /**
   * Navigate to previous week
   */
  const goToPreviousWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      // Use parseISO to avoid UTC parsing issues with date strings
      const date = parseISO(prev);
      const newDate = addDays(date, -7);
      return getWeekStart(newDate);
    });
  }, []);

  /**
   * Navigate to next week
   */
  const goToNextWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      // Use parseISO to avoid UTC parsing issues with date strings
      const date = parseISO(prev);
      const newDate = addDays(date, 7);
      return getWeekStart(newDate);
    });
  }, []);

  /**
   * Jump to current week
   */
  const goToCurrentWeek = useCallback(() => {
    setCurrentWeekStart(getWeekStart(new Date()));
  }, []);

  /**
   * Set specific week
   */
  const setWeek = useCallback((date: Date | string) => {
    // Use parseISO for strings to avoid UTC parsing issues
    const d = typeof date === 'string' ? parseISO(date) : date;
    setCurrentWeekStart(getWeekStart(d));
  }, []);

  /**
   * Check if current week is the current calendar week
   */
  const isCurrentWeek = useCallback(() => {
    return currentWeekStart === getWeekStart(new Date());
  }, [currentWeekStart]);

  /**
   * Get formatted week range string (e.g., "04-10 Nov 2025")
   */
  const getWeekRangeString = useCallback((): string => {
    // Use parseISO to avoid UTC parsing issues with date strings
    const start = parseISO(currentWeekStart);
    const end = addDays(start, 6);

    const startDay = start.getDate();
    const endDay = end.getDate();

    const monthNames = [
      'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
      'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
    ];

    const month = monthNames[end.getMonth()];
    const year = end.getFullYear();

    return `${startDay.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')} ${month} ${year}`;
  }, [currentWeekStart]);

  return {
    mode,
    switchMode,
    currentWeekStart,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    setWeek,
    isCurrentWeek,
    getWeekRangeString
  };
};
